import React, { useState, useEffect, useRef } from "react";
import CollectionSection from "@/components/tab/CollectionSection";
import OpenTabsPanel from "@/components/tab/OpenTabsPanel";
import CreateCollectionDialog from "@/components/tab/CreateCollectionDialog";
import { Collection, Tab } from "@/types";
import { toast } from "sonner";
import TabsNav from "@/components/tab/TabsNav";
import { api } from "@/services/api";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import TabCard from "@/components/tab/TabCard";
import { useCollections } from "@/hooks/useCollections";
import { useAllWindowsTabs } from "@/hooks/useAllWindowsTabs";
import { cn } from "@/lib/utils";
import FloatingNav from "@/components/common/FloatingNav";

const TabsPage = () => {
  const [collections, setCollections] = useCollections();
  const windows = useAllWindowsTabs();
  const [expandedCollections, setExpandedCollections] = useState<string[]>([]);
  const [showOpenTabs, setShowOpenTabs] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Only set initial expanded state on first load
  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current && collections?.length > 0) {
      setExpandedCollections([collections[0].collection_id]);
      didInit.current = true;
    }
  }, [collections]);

  const handleToggleExpand = (collectionId: string) => {
    setExpandedCollections((prev) =>
      prev.includes(collectionId)
        ? prev.filter((id) => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const handleExpandAll = () => {
    setExpandedCollections(collections.map((c) => c.collection_id));
  };

  const handleCollapseAll = () => {
    setExpandedCollections([]);
  };

  const handleCreateCollection = async (name: string) => {
    try {
      const response = await api.createCollection({ name });
      const refreshed = await api.getCollections();
      setCollections(refreshed.data);
      setExpandedCollections(
        response.data && response.data.collection_id
          ? Array.from(new Set([...expandedCollections, response.data.collection_id]))
              .filter(id => id && refreshed.data.some(c => c.collection_id === id))
          : expandedCollections
      );
      toast.success(
        response.data && response.data.collection_id
          ? `"${name}" collection has been created successfully.`
          : "Collection created."
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create collection');
    }
  };

  const handleUpdateCollection = (updatedCollection: Collection) => {
    setCollections(
      collections.map(collection => 
        collection.collection_id === updatedCollection.collection_id ? updatedCollection : collection
      )
    );
  };

  const handleDeleteCollection = (collectionId: string) => {
    setCollections(collections.filter(c => c.collection_id !== collectionId));
    setExpandedCollections(prev => prev.filter(id => id !== collectionId));
    toast.success("Collection deleted successfully");
  };

  const handleDeleteTab = (collectionId: string, tabId: string) => {
    setCollections(collections.map(collection => {
      if (collection.collection_id === collectionId) {
        return {
          ...collection,
          tabs: (collection.tabs ?? []).filter(tab => tab.tab_id !== tabId)
        };
      }
      return collection;
    }));
    toast.success("Tab deleted successfully");
  };

  // Edit tab handler
  const handleTabEdit = async (tabId: string, updated: Partial<Tab> & { favicon_url?: string }) => {
    // Find the tab in collections to get its full data
    let originalTab: Tab | undefined;
    for (const c of collections) {
      const found = c.tabs?.find(t => t.tab_id === tabId);
      if (found) {
        originalTab = found;
        break;
      }
    }
    if (!originalTab) {
      toast.error("Tab not found");
      return;
    }
    const fullTab: Tab = {
      ...originalTab,
      ...updated,
      favicon_url: updated.favicon_url ?? originalTab.favicon_url,
    };
    try {
      await api.createOrUpdateTab(fullTab);
      toast.success("Tab updated");
      const refreshed = await api.getCollections();
      setCollections(refreshed.data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update tab");
    }
  };

  // Delete tab handler
  const handleTabDelete = async (tab: Tab) => {
    try {
      await api.createOrUpdateTab({ ...tab, status: "INACTIVE" });
      toast.success("Tab deleted");
      const refreshed = await api.getCollections();
      setCollections(refreshed.data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete tab");
    }
  };

  // react-beautiful-dnd drag end handler
  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Check if source is a collection or an open window
    const sourceCollection = collections.find(c => c.collection_id === source.droppableId);
    const destCollection = collections.find(c => c.collection_id === destination.droppableId);
    const isSourceCollection = !!sourceCollection;
    const isDestCollection = !!destCollection;

    // If dropping into a collection from open tabs (window)
    if (!isSourceCollection && isDestCollection) {
      // Find the tab in open windows
      const sourceWindow = windows.find(w => w.id === source.droppableId);
      const chromeTab = sourceWindow?.tabs.find(t => t.id === draggableId);
      if (!chromeTab) return;
      // Convert to Tab
      const { chromeTabToTab } = await import("@/lib/utils");
      const tab = chromeTabToTab(chromeTab);
      // Insert into destination collection at the right index
      const newTabs = Array.from(destCollection.tabs ?? []);
      newTabs.splice(destination.index, 0, {
        ...tab,
        collection_id: destCollection.collection_id,
        sequence: destination.index + 1,
        status: "ACTIVE",
      });
      // Persist to backend
      try {
        await api.createOrUpdateTab({
          collection_id: destCollection.collection_id,
          title: tab.title,
          url: tab.url,
          favicon_url: tab.favicon_url,
          sequence: destination.index + 1,
          status: "ACTIVE",
        });
        toast.success("Tab added to collection");
        // Refresh collections
        const refreshed = await api.getCollections();
        setCollections(refreshed.data);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to add tab");
      }
      return;
    }

    // If both source and destination are collections (reorder or move)
    if (isSourceCollection && isDestCollection) {
      // Find the tab being moved
      const tab = sourceCollection.tabs?.find(t => t.tab_id === draggableId);
      if (!tab) return;
      // Remove tab from source
      let newSourceTabs = (sourceCollection.tabs ?? []).filter(t => t.tab_id !== draggableId);
      // Insert tab into destination
      let newDestTabs = Array.from(destCollection.tabs ?? []);
      newDestTabs.splice(destination.index, 0, tab);
      // Update collections state
      const newCollections = collections.map(c => {
        if (c.collection_id === source.droppableId) {
          return { ...c, tabs: newSourceTabs };
        } else if (c.collection_id === destination.droppableId) {
          return { ...c, tabs: newDestTabs };
        } else {
          return c;
        }
      });
      setCollections(newCollections);
      // Persist changes to backend (update sequence for all tabs in both collections)
      try {
        await Promise.all([
          ...newSourceTabs.map((t, idx) =>
            api.createOrUpdateTab({
              tab_id: t.tab_id,
              collection_id: source.droppableId,
              title: t.title,
              url: t.url,
              favicon_url: t.favicon_url,
              sequence: idx + 1,
              status: t.status,
            })
          ),
          ...newDestTabs.map((t, idx) =>
            api.createOrUpdateTab({
              tab_id: t.tab_id,
              collection_id: destination.droppableId,
              title: t.title,
              url: t.url,
              favicon_url: t.favicon_url,
              sequence: idx + 1,
              status: t.status,
            })
          )
        ]);
        toast.success('Tab moved');
        const refreshed = await api.getCollections();
        setCollections(refreshed.data);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to update tab order');
      }
      return;
    }
    // (Optional) handle other cases if needed
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className={cn(
        "flex flex-col min-h-screen",
        "bg-background text-foreground"
      )}>
        <TabsNav 
          onCreateCollection={() => setCreateDialogOpen(true)}
          onToggleOpenTabs={() => setShowOpenTabs(!showOpenTabs)}
          showOpenTabs={showOpenTabs}
        />
        <div className="mt-14">
          <div className="flex-1 relative">
            <main className="h-full overflow-y-auto">
              <div className={cn(
                "px-4 md:px-6 py-4 transition-all duration-200",
                showOpenTabs ? "md:pr-[400px]" : ""
              )}>
                {collections?.filter(Boolean).map((collection) =>
                  collection && collection.collection_id ? (
                    <CollectionSection
                      key={collection.collection_id}
                      collection={collection}
                      isExpanded={expandedCollections.includes(collection.collection_id)}
                      onToggleExpand={() => handleToggleExpand(collection.collection_id)}
                      onUpdateCollection={handleUpdateCollection}
                      onDeleteCollection={handleDeleteCollection}
                      onDeleteTab={handleDeleteTab}
                      onTabEdit={handleTabEdit}
                      onTabDelete={handleTabDelete}
                    />
                  ) : null
                )}
              </div>
            </main>
            {showOpenTabs && (
              <OpenTabsPanel
                windows={windows}
                onClose={() => setShowOpenTabs(false)}
              />
            )}
          </div>
          <CreateCollectionDialog
            open={createDialogOpen}
            onOpenChange={setCreateDialogOpen}
            onCreateCollection={handleCreateCollection}
          />
          <FloatingNav
            onToggleTabs={() => setShowOpenTabs(!showOpenTabs)}
            onToggleNotes={() => {}}
            showTabs={showOpenTabs}
            showNotes={false}
          />
        </div>
      </div>
    </DragDropContext>
  );
};

export default TabsPage; 